import React, {useState, useReducer, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {routeNodeSelector, actions as routerActions} from 'redux-router5';
import {Button, Divider, Form, Grid, Header, Segment} from 'semantic-ui-react';

import {formValidation} from 'helpers';
import {ProcessorTypes} from 'helpers/constants';
import {addressActions, paymentActions as actions} from 'actions';

import AuthForm from './AuthForm';
import SquareFormWrapper from './SquareForm/Wrapper';
import {Loading} from 'Common/Structure';
import {CurrencyValue} from 'Common/Format';
import StripeFormWrapper from './StripeForm/Wrapper';

import './collectionForm.scss';
import {useAsyncLoad} from 'helpers/customHooks';
import AddressForm from './AddressForm';

const initialState = (allRequired = false) => {
  const requiredFields = allRequired
    ? {
        address1: {
          displayName: 'Address 1',
          isRequired: true,
          value: '',
        },
        city: {
          displayName: 'City',
          isRequired: true,
          value: '',
        },
        state: {
          displayName: 'State',
          isRequired: true,
          value: '',
        },
        zip: {
          displayName: 'Postal Code',
          isRequired: true,
          value: '',
        },
        email: {
          displayName: 'Email Address',
          isRequired: true,
          value: '',
        },
      }
    : {};

  return {
    countryId: {
      displayName: 'Country',
      isRequired: allRequired,
      value: 'US',
    },
    firstName: {
      displayName: 'First Name',
      isRequired: true,
    },
    lastName: {
      displayName: 'Last Name',
      isRequired: true,
    },
    ...requiredFields,
  };
};

const PaymentForm = props => {
  const [state, setState] = useReducer(
    (currentState, newState) => ({...currentState, ...newState}),
    {
      formLoading: false,
      showBillingAddress: true,
      submitButtonEnabled: false,
    },
  );
  const [formObject, setFormObject] = useState(null);
  const countries = useAsyncLoad(props.loadCountries);
  const [processorData, setProcessorData] = useState(undefined);

  // processor settings
  const gateway = useAsyncLoad(
    props.initiatePayment,
    props.router.route.params.amount,
    props.router.route.params.estimateId,
    props.router.route.params.invoiceId,
    Number(props.router.route.params.paymentMethod),
    props.router.route.params.receiptEmails,
  );
  const processorSettings = gateway.data && gateway.data.data;

  const isProcessor = type =>
    processorSettings &&
    processorSettings.gatewayType.toLowerCase() === type.toLowerCase();

  const isWePay = isProcessor(ProcessorTypes.WePay);
  const isStripe = isProcessor(ProcessorTypes.Stripe);
  const isAuth =
    isProcessor(ProcessorTypes.Auth) || isProcessor('AuthorizeNet');
  const isSquare = isProcessor(ProcessorTypes.Square);

  // startup
  useEffect(() => {
    if (!props.router.route.params.customerId) {
      throw new Error('Missing customer');
    }

    initialLoad();
  }, []);

  const initialLoad = async () => {
    const response = await gateway.load();

    if (
      response.data.gatewayType.toLowerCase() ==
        ProcessorTypes.WePay.toLowerCase() &&
      response.data.redirectUrl
    ) {
      window.top.location.href = response.data.redirectUrl;
      return;
    }

    setFormObject(
      formValidation.setupNewFormObject(
        initialState(
          response.data &&
            response.data.gatewayType.toLowerCase() ===
              ProcessorTypes.Square.toLowerCase(),
        ),
        props.router.route.params,
      ),
    );

    countries.load();
  };

  // card on file
  const cardOnFile = useAsyncLoad(
    props.getCardOnFile,
    props.router.route.params.customerId,
    isSquare,
  );
  useEffect(
    () => {
      if (isStripe || isSquare) {
        cardOnFile.load();
      }
    },
    [isStripe, isSquare],
  );

  // stripe setup
  useEffect(
    () => {
      if (isStripe && formObject && !formObject.stripeCurrency) {
        updateField(processorSettings.currency, 'stripeCurrency');
        updateField('0', 'stripeCustomerId');
      }
    },
    [processorSettings, formObject, isStripe],
  );

  // form
  const updateField = (value, field) =>
    setFormObject(prev => formValidation.updateFormObject(prev, field, value));

  const handleSubmit = async e => {
    e.preventDefault();

    setState({formLoading: true});

    const result = await props.submitPayment({
      auth: isAuth ? processorData : null,
      paymentObject:
        isSquare &&
        processorData.selectedCardId &&
        processorData.selectedCardId !== '0'
          ? formValidation.requireNone(formObject)
          : formObject,
      stripe: isStripe ? processorData : null,
      square: isSquare ? processorData : null,
    });

    setState({formLoading: false});

    if (!result.error) {
      props.navigateTo('completepayment', {
        amount: formObject.amount.value,
        estimateNumber: props.router.route.params.estimateNumber,
        invoiceNumber: props.router.route.params.invoiceNumber,
        portalName: props.portalName,
        square: isSquare,
      });
    } else {
      setProcessorData(null);
    }
  };

  const handleCancel = () => {
    const {
      amount,
      fullAmount,
      receiptEmails,
      sendReceipt,
    } = props.router.route.params;
    props.navigateTo(props.previousRoute.name, {
      ...props.previousRoute.params,
      amount,
      fullAmount,
      receiptEmails,
      sendReceipt,
    });
  };

  // card on file
  const handleChangeCardSelection = (e, {value}) => {
    const updatedForm = formValidation.updateFormObject(
      formObject,
      'stripeCustomerId',
      value,
    );
    setFormObject({
      ...updatedForm,
      firstName: {
        ...formObject.firstName,
        isRequired: value === '0',
      },
      lastName: {
        ...formObject.lastName,
        isRequired: value === '0',
      },
    });
    setState({
      submitButtonEnabled: value !== '0',
      showBillingAddress: value === '0',
    });

    setProcessorData(prev => ({
      ...prev,
      card: value !== '0' ? null : prev.card,
    }));
  };

  const handleChangeSquareCardSelection = value => {
    setState({
      submitButtonEnabled: value !== '0',
      showBillingAddress: value === '0',
    });
    setProcessorData(prev => ({
      ...prev,
      selectedCardId: value,
    }));
  };

  return (
    <div style={{padding: '0 15px'}}>
      <Grid centered>
        <Grid.Column computer={10} largeScreen={10} mobile={16}>
          <Segment className={isWePay ? 'wepay-wrapper' : ''}>
            {gateway.loading || countries.loading ? (
              <Loading inline />
            ) : isStripe || isAuth || isSquare ? (
              <Form onSubmit={handleSubmit}>
                <Header as="h2" style={{marginBottom: 15}}>
                  Payment Information
                </Header>

                <Header as="h1" textAlign="center">
                  <CurrencyValue
                    value={Number(props.router.route.params.amount)}
                  />
                </Header>

                <Grid>
                  <Grid.Column width={16}>
                    <Form.Field>
                      <label>Credit Card</label>
                      {isStripe ? (
                        <StripeFormWrapper
                          cardOnFile={cardOnFile.data || null}
                          onChangeCardSelection={handleChangeCardSelection}
                          selectedCard={
                            (formObject.stripeCustomerId &&
                              formObject.stripeCustomerId.value) ||
                            '0'
                          }
                          setProcessor={setProcessorData}
                          showCreditCard={state.showBillingAddress}
                          stripe={processorData}
                          stripeData={processorSettings}
                          updateSubmitButtonEnabled={submitButtonEnabled =>
                            setState({submitButtonEnabled})
                          }
                        />
                      ) : isAuth ? (
                        <AuthForm
                          auth={processorData}
                          clientKey={processorSettings.key}
                          loginKey={processorSettings.login}
                          setProcessor={setProcessorData}
                        />
                      ) : isSquare ? (
                        <SquareFormWrapper
                          applicationId={processorSettings.key}
                          locationId={processorSettings.squareLocationId}
                          cardOnFile={cardOnFile.data || null}
                          data={processorData}
                          onChangeCardSelection={
                            handleChangeSquareCardSelection
                          }
                          setProcessor={obj =>
                            setProcessorData(prev => ({
                              ...prev,
                              ...obj,
                            }))
                          }
                          showCreditCard={state.showBillingAddress}
                          updateSubmitButtonEnabled={submitButtonEnabled =>
                            setState({submitButtonEnabled})
                          }
                        />
                      ) : null}
                    </Form.Field>
                  </Grid.Column>
                </Grid>

                {state.showBillingAddress && (
                  <AddressForm
                    countries={countries}
                    formObject={formObject}
                    processorName={processorSettings.gatewayType}
                    showSaveCard={isStripe || isSquare}
                    updateField={updateField}
                  />
                )}

                <Divider style={{margin: '20px 0'}} />

                <div style={{textAlign: 'center'}}>
                  <Button
                    content="Submit Payment"
                    disabled={
                      state.formLoading ||
                      (isStripe && !state.submitButtonEnabled) ||
                      (isAuth && !processorData) ||
                      (isSquare && !processorData)
                    }
                    loading={state.formLoading}
                    positive
                    size="large"
                    type="submit"
                  />

                  <div style={{marginTop: 15}}>
                    <a href="javascript:void(0)" onClick={handleCancel}>
                      Back
                    </a>
                  </div>
                </div>
              </Form>
            ) : isWePay && processorSettings.iFrameUrl ? (
              <iframe
                frameBorder="0"
                src={processorSettings.iFrameUrl}
                style={{width: '100%', height: 400, marginBottom: -5}}
              />
            ) : null}
          </Segment>
        </Grid.Column>
      </Grid>
    </div>
  );
};

PaymentForm.defaultProps = {
  previousRoute: {},
};

PaymentForm.propTypes = {
  portalName: PropTypes.string.isRequired,
  previousRoute: PropTypes.shape({name: PropTypes.string.isRequired}),
  router: PropTypes.shape({}).isRequired,

  // functions
  getCardOnFile: PropTypes.func.isRequired,
  initiatePayment: PropTypes.func.isRequired,
  loadCountries: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  submitPayment: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  ...routeNodeSelector('payment')(state),
  portalName: state.portal.portalInfo.name,
  router: state.router,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getCardOnFile: actions.getCardOnFile,
      initiatePayment: actions.initiatePayment,
      loadCountries: addressActions.loadCountries,
      navigateTo: routerActions.navigateTo,
      submitPayment: actions.submitPayment,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PaymentForm);



// WEBPACK FOOTER //
// ./src/components/Payments/PaymentCollection/components/PaymentForm/CollectionForm.js